/**
 * Supplement data transformation utilities
 * 
 * Transforms SupplementDetailed data from the API into display-friendly structures.
 * All transformation logic is isolated here to match the comparison pattern.
 */

import type { SupplementDetailed, SupplementJustification } from '@/api/generated/client/models';
import type {
  DisplayLineItem,
  DisplayRoom,
  DisplayTotals,
  ComparisonSnapshot,
  SnapshotMatchGroup,
  SnapshotRoomMatchGroup,
  SnapshotLineItem,
  LineItemDetails,
  LineItemCategory,
} from '@/types/supplement';

interface RoomData {
  items: DisplayLineItem[];
  contractorRoomNames?: string[];
  insurerRoomNames?: string[];
}

/**
 * Build display name from room name arrays
 * Shows both contractor and insurer rooms if they differ
 * Arrays should already be sorted before calling this
 */
function buildRoomDisplayName(
  contractorNames: string[],
  insurerNames: string[],
  fallback: string = 'Unknown Room'
): string {
  if (contractorNames.length > 0 && insurerNames.length > 0) {
    const contractorNamesStr = contractorNames.join(", ");
    const insurerNamesStr = insurerNames.join(", ");
    if (contractorNamesStr !== insurerNamesStr) {
      return `${contractorNamesStr} (Contractor) | ${insurerNamesStr} (Insurance)`;
    }
    return contractorNamesStr;
  }
  if (contractorNames.length > 0) {
    return contractorNames.join(", ");
  }
  if (insurerNames.length > 0) {
    return insurerNames.join(", ");
  }
  return fallback;
}

/**
 * Create LineItemDetails from SnapshotLineItem
 */
function createLineItemDetails(lineItem: SnapshotLineItem): LineItemDetails {
  return {
    lineItemId: lineItem.lineItemId,
    lineItemNumber: lineItem.lineItemNumber,
    quantity: Number(lineItem.qty) || 0,
    uom: lineItem.uom,
    unitPriceCents: lineItem.unitPriceCents,
    rcvCents: lineItem.rcvCents,
    description: lineItem.description,
  };
}

/**
 * Determine the category based on contractor vs insurer RCV values
 * Note: This should only be called for MATCHED items (items in a match group)
 * Unmatched items should use 'unmatched_contractor' or 'unmatched_insurer' directly
 */
function determineCategory(
  contractorRcvCents: number,
  insurerRcvCents: number
): LineItemCategory {
  // Perfect match
  if (contractorRcvCents === insurerRcvCents) {
    return 'perfect_match';
  }
  
  // Price discrepancy (underscoped) - contractor wants more
  if (contractorRcvCents > insurerRcvCents) {
    return 'underscoped';
  }
  
  // Overscoped (insurer > contractor) - insurer offered more
  return 'overscoped';
}

/**
 * Create a DisplayLineItem from a match group
 */
function createDisplayLineItem(
  matchGroup: SnapshotMatchGroup,
  roomName: string,
  justification?: SupplementJustification
): DisplayLineItem {
  const contractorItems = (matchGroup.contractorLineItems || []).map(createLineItemDetails);
  const insurerItems = (matchGroup.insurerLineItems || []).map(createLineItemDetails);
  
  // Calculate total RCV for contractor and insurer
  const contractorTotal = contractorItems.reduce((sum, item) => sum + item.rcvCents, 0);
  const insurerTotal = insurerItems.reduce((sum, item) => sum + item.rcvCents, 0);
  
  // Determine category based on price comparison
  const category = determineCategory(contractorTotal, insurerTotal);
  
  // Amount is the justification amount if exists, otherwise the contractor total
  const amountCents = justification 
    ? (justification.amountRequestedCents || 0)
    : contractorTotal;
  
  return {
    insurerLineItemIds: matchGroup.insurerLineItems?.map((li) => li.lineItemId) || [],
    contractorLineItemIds: matchGroup.contractorLineItems?.map((li) => li.lineItemId) || [],
    lineItemMatchGroupId: matchGroup.id, // Add match group ID for matched items
    category,
    justification,
    amountCents,
    agreedUponAmountCents: insurerTotal > 0 ? insurerTotal : undefined,
    description: contractorItems[0]?.description || insurerItems[0]?.description || '',
    roomName,
    contractorItems,
    insurerItems,
  };
}

/**
 * Process room match groups from the snapshot
 */
function processRoomMatchGroups(
  roomGroups: SnapshotRoomMatchGroup[],
  justificationByMatchGroupId: Map<number, SupplementJustification>,
  justificationByLineItemId: Map<number, SupplementJustification>,
  processedInsurerItemIds: Set<number>,
  processedContractorItemIds: Set<number>,
  roomsMap: Map<string, RoomData>
): void {
  roomGroups.forEach((roomGroup) => {
    const contractorRoomNames = (roomGroup.contractorRooms?.map(r => r.name).filter(Boolean) || []).sort();
    const insurerRoomNames = (roomGroup.insurerRooms?.map(r => r.name).filter(Boolean) || []).sort();
    const roomKey = `rmg-${roomGroup.id}`;
    
    roomGroup.lineItemMatchGroups?.forEach((matchGroup) => {
      // Build room name from arrays for this item
      const roomName = buildRoomDisplayName(contractorRoomNames, insurerRoomNames);
      
      // Look up justification by match group ID first, then fall back to line item ID
      // (migration may have assigned lineItemId instead of lineItemMatchGroupId)
      let justification = justificationByMatchGroupId.get(matchGroup.id);
      if (!justification) {
        const allLineItemIds = [
          ...(matchGroup.contractorLineItems || []).map(li => li.lineItemId),
          ...(matchGroup.insurerLineItems || []).map(li => li.lineItemId),
        ];
        for (const liId of allLineItemIds) {
          justification = justificationByLineItemId.get(liId);
          if (justification) break;
        }
      }
      
      const item = createDisplayLineItem(
        matchGroup,
        roomName,
        justification
      );
      
      // Mark items as processed
      matchGroup.contractorLineItems?.forEach((li) => processedContractorItemIds.add(li.lineItemId));
      matchGroup.insurerLineItems?.forEach((li) => processedInsurerItemIds.add(li.lineItemId));
      
      if (!roomsMap.has(roomKey)) {
        roomsMap.set(roomKey, { 
          items: [], 
          contractorRoomNames, 
          insurerRoomNames 
        });
      }
      roomsMap.get(roomKey)!.items.push(item);
    });
  });
}

/**
 * Process standalone match groups (not in rooms)
 */
function processStandaloneMatchGroups(
  matchGroups: SnapshotMatchGroup[],
  justificationByMatchGroupId: Map<number, SupplementJustification>,
  processedInsurerItemIds: Set<number>,
  processedContractorItemIds: Set<number>,
  roomsMap: Map<string, RoomData>,
  roomIdToGroupKey: Map<number, string>
): void {
  matchGroups.forEach((matchGroup) => {
    // Collect room names from all line items in this match group (sorted for consistency)
    const contractorRoomNames = (matchGroup.contractorLineItems || [])
      .map(li => li.roomName)
      .filter((name): name is string => !!name)
      .sort();
    const insurerRoomNames = (matchGroup.insurerLineItems || [])
      .map(li => li.roomName)
      .filter((name): name is string => !!name)
      .sort();
    
    // Get roomId to check if this should join an existing room match group
    const firstLineItem = matchGroup.contractorLineItems?.[0] || matchGroup.insurerLineItems?.[0];
    const roomId = firstLineItem?.roomId;
    
    // Check if this roomId belongs to a room match group, otherwise use standalone key
    const roomKey = roomId && roomIdToGroupKey.has(roomId) 
      ? roomIdToGroupKey.get(roomId)! 
      : `standalone-${matchGroup.id}`;
    
    // Calculate room name from arrays
    const roomName = buildRoomDisplayName(contractorRoomNames, insurerRoomNames, 'Standalone Items');
    
    const item = createDisplayLineItem(
      matchGroup,
      roomName,
      justificationByMatchGroupId.get(matchGroup.id)
    );
    
    // Mark items as processed
    matchGroup.contractorLineItems?.forEach((li) => processedContractorItemIds.add(li.lineItemId));
    matchGroup.insurerLineItems?.forEach((li) => processedInsurerItemIds.add(li.lineItemId));
    
    if (!roomsMap.has(roomKey)) {
      roomsMap.set(roomKey, { 
        items: [], 
        contractorRoomNames, 
        insurerRoomNames 
      });
    }
    roomsMap.get(roomKey)!.items.push(item);
  });
}

/**
 * Process unmatched items (items on one side only)
 */
function processUnmatchedItems(
  unmatchedItems: { insurerLineItems?: SnapshotLineItem[]; contractorLineItems?: SnapshotLineItem[] } | undefined,
  justificationByLineItemId: Map<number, SupplementJustification>,
  processedInsurerItemIds: Set<number>,
  processedContractorItemIds: Set<number>,
  roomsMap: Map<string, RoomData>,
  roomIdToGroupKey: Map<number, string>
): void {
  if (!unmatchedItems) return;
  
  // Process unmatched contractor items
  unmatchedItems.contractorLineItems?.forEach((lineItem) => {
    if (processedContractorItemIds.has(lineItem.lineItemId)) return;
    
    const roomName = lineItem.roomName || 'Unmatched Items';
    const roomId = lineItem.roomId;
    
    // If this roomId belongs to a room match group, add to that group
    // Otherwise create a separate unmatched section
    const roomKey = roomId && roomIdToGroupKey.has(roomId) 
      ? roomIdToGroupKey.get(roomId)! 
      : `unmatched-contractor-${roomId || roomName}`;
    
    const justification = justificationByLineItemId.get(lineItem.lineItemId);
    
    const item: DisplayLineItem = {
      insurerLineItemIds: [],
      contractorLineItemIds: [lineItem.lineItemId],
      lineItemId: lineItem.lineItemId,
      category: 'unmatched_contractor',
      justification,
      amountCents: justification ? (justification.amountRequestedCents || 0) : lineItem.rcvCents,
      description: lineItem.description,
      roomName,
      contractorItems: [createLineItemDetails(lineItem)],
      insurerItems: [],
    };
    
    if (!roomsMap.has(roomKey)) {
      roomsMap.set(roomKey, { 
        items: [], 
        contractorRoomNames: [roomName], 
        insurerRoomNames: [] 
      });
    }
    roomsMap.get(roomKey)!.items.push(item);
  });
  
  // Process unmatched insurer items
  unmatchedItems.insurerLineItems?.forEach((lineItem) => {
    if (processedInsurerItemIds.has(lineItem.lineItemId)) return;
    
    const roomName = lineItem.roomName || 'Unmatched Items';
    const roomId = lineItem.roomId;
    
    // If this roomId belongs to a room match group, add to that group
    // Otherwise create a separate unmatched section
    const roomKey = roomId && roomIdToGroupKey.has(roomId) 
      ? roomIdToGroupKey.get(roomId)! 
      : `unmatched-insurer-${roomId || roomName}`;
    
    const justification = justificationByLineItemId.get(lineItem.lineItemId);
    
    const item: DisplayLineItem = {
      insurerLineItemIds: [lineItem.lineItemId],
      contractorLineItemIds: [],
      lineItemId: lineItem.lineItemId,
      category: 'unmatched_insurer',
      justification,
      amountCents: justification ? (justification.amountRequestedCents || 0) : 0,
      description: lineItem.description,
      roomName,
      contractorItems: [],
      insurerItems: [createLineItemDetails(lineItem)],
    };
    
    if (!roomsMap.has(roomKey)) {
      roomsMap.set(roomKey, { 
        items: [], 
        contractorRoomNames: [], 
        insurerRoomNames: [roomName] 
      });
    }
    roomsMap.get(roomKey)!.items.push(item);
  });
}

/**
 * Build the final display result from the rooms map
 */
function buildDisplayResult(roomsMap: Map<string, RoomData>): {
  rooms: DisplayRoom[];
  totals: DisplayTotals;
} {
  const rooms: DisplayRoom[] = Array.from(roomsMap.values()).map((roomData) => {
    // Build room name from contractor and insurer room names (already sorted)
    const contractorNames = roomData.contractorRoomNames || [];
    const insurerNames = roomData.insurerRoomNames || [];
    
    const roomName = buildRoomDisplayName(
      contractorNames,
      insurerNames,
      roomData.items[0]?.roomName || 'Unknown Room'
    );
    
    return {
      roomName,
      contractorRoomNames: roomData.contractorRoomNames,
      insurerRoomNames: roomData.insurerRoomNames,
      items: roomData.items,
      // Calculate supplement amount - sum of (contractor - insurer) for unmatched_contractor and underscoped
      supplementAmountCents: roomData.items
        .filter(i => i.category === 'unmatched_contractor' || i.category === 'underscoped')
        .reduce((sum, i) => {
          const contractorTotal = i.contractorItems.reduce((s, item) => s + item.rcvCents, 0);
          const insurerTotal = i.insurerItems.reduce((s, item) => s + item.rcvCents, 0);
          return sum + (contractorTotal - insurerTotal);
        }, 0),
      // Calculate money found - free scoped items (unmatched_insurer) + overscope differences (insurer - contractor when insurer > contractor)
      moneyFoundCents: roomData.items.reduce((sum, i) => {
        if (i.category === 'unmatched_insurer') {
          // Free scoped items - full insurer amount
          const insurerTotal = i.insurerItems.reduce((itemSum, item) => itemSum + item.rcvCents, 0);
          return sum + insurerTotal;
        } else if (i.category === 'overscoped') {
          // Overscoped items - insurer amount minus contractor amount (positive difference)
          const contractorTotal = i.contractorItems.reduce((s, item) => s + item.rcvCents, 0);
          const insurerTotal = i.insurerItems.reduce((s, item) => s + item.rcvCents, 0);
          return sum + Math.abs(insurerTotal - contractorTotal);
        }
        return sum;
      }, 0),
    };
  });
  
  const totals: DisplayTotals = {
    totalSupplementCents: rooms.reduce((sum, r) => sum + r.supplementAmountCents, 0),
    unmatchedContractorCount: rooms.reduce((sum, r) => sum + r.items.filter(i => i.category === 'unmatched_contractor').length, 0),
    underscopedCount: rooms.reduce((sum, r) => sum + r.items.filter(i => i.category === 'underscoped').length, 0),
    overscopedCount: rooms.reduce((sum, r) => sum + r.items.filter(i => i.category === 'overscoped').length, 0),
    unmatchedInsurerCount: rooms.reduce((sum, r) => sum + r.items.filter(i => i.category === 'unmatched_insurer').length, 0),
    perfectMatchCount: rooms.reduce((sum, r) => sum + r.items.filter(i => i.category === 'perfect_match').length, 0),
  };
  
  return { rooms, totals };
}

/**
 * Transform SupplementDetailed into display-friendly structure
 * 
 * KEY RULES:
 * 1. Items in supplement_justification table are "supplemented"
 * 2. All other items from snapshot are "agreed_upon"
 * 3. Use snapshot data exclusively (so comparison updates don't affect supplement)
 * 4. Display ALL line items from the comparison snapshot
 */
export function transformSupplementForDisplay(supplement: SupplementDetailed): {
  rooms: DisplayRoom[];
  totals: DisplayTotals;
} {
  if (!supplement.comparisonSnapshot) {
    return { 
      rooms: [], 
      totals: { 
        totalSupplementCents: 0, 
        unmatchedContractorCount: 0, 
        underscopedCount: 0, 
        overscopedCount: 0, 
        unmatchedInsurerCount: 0, 
        perfectMatchCount: 0 
      } 
    };
  }

  const justifications = supplement.justifications || [];
  const snapshot = supplement.comparisonSnapshot as ComparisonSnapshot;
  
  // Create lookup maps for justifications
  const justificationByMatchGroupId = new Map<number, SupplementJustification>();
  const justificationByLineItemId = new Map<number, SupplementJustification>();
  
  // Track processed line items to avoid duplication
  const processedInsurerItemIds = new Set<number>();
  const processedContractorItemIds = new Set<number>();

  justifications.forEach((j: SupplementJustification) => {
    if (j.lineItemMatchGroupId) {
      justificationByMatchGroupId.set(j.lineItemMatchGroupId, j);
    }
    if (j.lineItemId) {
      justificationByLineItemId.set(j.lineItemId, j);
    }
  });

  const roomsMap = new Map<string, RoomData>();
  
  // Build a map of roomId -> room match group key
  // This tells us which room match group each roomId belongs to
  const roomIdToGroupKey = new Map<number, string>();
  
  // First pass: map all roomIds to their room match group
  snapshot.roomMatchGroups?.forEach(roomGroup => {
    const roomKey = `rmg-${roomGroup.id}`;
    
    // Map room IDs declared in the room match group itself
    // This ensures rooms that are part of a match but have no matched line items
    // are still associated with the correct group (prevents duplicate room display)
    roomGroup.contractorRooms?.forEach(room => {
      if (room.id) roomIdToGroupKey.set(room.id, roomKey);
    });
    roomGroup.insurerRooms?.forEach(room => {
      if (room.id) roomIdToGroupKey.set(room.id, roomKey);
    });
    
    // Also collect roomIds from line items within this room match group
    roomGroup.lineItemMatchGroups?.forEach(matchGroup => {
      [...(matchGroup.contractorLineItems || []), ...(matchGroup.insurerLineItems || [])].forEach(item => {
        if (item.roomId) {
          roomIdToGroupKey.set(item.roomId, roomKey);
        }
      });
    });
  });
  
  // Process matched line item groups within rooms
  processRoomMatchGroups(
    snapshot.roomMatchGroups || [],
    justificationByMatchGroupId,
    justificationByLineItemId,
    processedInsurerItemIds,
    processedContractorItemIds,
    roomsMap
  );
  
  // Process standalone line item match groups (not within rooms)
  processStandaloneMatchGroups(
    snapshot.standaloneLineItemMatchGroups || [],
    justificationByMatchGroupId,
    processedInsurerItemIds,
    processedContractorItemIds,
    roomsMap,
    roomIdToGroupKey
  );
  
  // Process unmatched items
  processUnmatchedItems(
    snapshot.unmatchedItems,
    justificationByLineItemId,
    processedInsurerItemIds,
    processedContractorItemIds,
    roomsMap,
    roomIdToGroupKey
  );
  
  // Convert to rooms array and calculate totals
  return buildDisplayResult(roomsMap);
}
